/**
 * Per-agent authorization ("Connected AI agents").
 *
 * WorkOS AuthKit cannot mint custom OAuth scopes, so per-agent permissioning is
 * enforced server-side: each connected agent (identified by its OAuth
 * `client_id`, resolved via token introspection) gets a grant that NARROWS the
 * acting user's RBAC ceiling. Effective access for a tool =
 *   grant.allowedScopes ∋ tool.scope  ∧  RBAC  ∧  FGA  ∧  visibility.
 *
 * This module defines the shared contracts (enum + interfaces) used by the MCP
 * server (enforcement), the platform API (management), and the web UI. The
 * class-validator request DTO lives in `apps/api` and imports the enum here
 * (per repo rule: shared enums live in `@alinea/types`).
 */
import { McpAgentConnectionStatus } from '../generated/enums.js';
import { MCP_TOOL_AUTH, McpOAuthScope } from './mcp.js';

// `McpAgentConnectionStatus` is generated from the Prisma enum
// (`sys_mcp_agent_grant.status`) and re-exported by the package barrel via
// `generated/enums.js`, so the DB, API, and UI share one source. Status values:
// PENDING (awaiting approval — least-privilege default), APPROVED (allowedScopes
// honored), REVOKED (all calls denied). Imported here only for type annotations.

/**
 * A connected agent and the scopes a tenant admin has granted it. Persisted in
 * `sys_mcp_agent_grant`, uniquely identified for active rows by
 * `(tenantId, userId, agentKey)` and addressed operationally by `grantId`.
 */
export interface McpAgentGrant {
  /** Stable row identity used by admin mutations. */
  grantId: string;
  /** Local ERP user who connected this client; null means legacy/unattributed. */
  connectedUser: {
    userId: string;
    name: string;
    email: string;
  } | null;
  /** True when the row predates per-user grant attribution. */
  isLegacy: boolean;
  /** Stable agent identity (OAuth `client_id` via introspection; fallbacks apply). */
  agentKey: string;
  /** Human label (from introspection `client_name`, renamable in the UI). */
  label: string | null;
  /** OAuth client name, kept separate from the editable label and agentKey. */
  clientName: string | null;
  status: McpAgentConnectionStatus;
  /** Scopes the agent has attempted to use (accumulated as it calls tools). */
  requestedScopes: McpOAuthScope[];
  /** Scopes the admin has granted this agent. Intersected with RBAC/FGA. */
  allowedScopes: McpOAuthScope[];
  /** ISO timestamp first observed. */
  firstSeenAt: string;
  /** ISO timestamp most recently observed. */
  lastSeenAt: string;
  /** ISO timestamp of approval, when approved. */
  approvedAt: string | null;
  /** Acting user id (local `sys_user.userId`) who approved, when approved. */
  approvedBy: string | null;
}

/** One selectable scope in the management UI, with its underlying tools. */
export interface McpAgentScopeCatalogEntry {
  scope: McpOAuthScope;
  /** Human label for the scope (UI), in English. */
  label: string;
  /** The same label in Brazilian Portuguese. */
  labelPtBr?: string;
  /** MCP tool names this scope unlocks. */
  tools: string[];
}

/** Connection details an agent (Claude/ChatGPT) needs to connect to MCP. */
export interface McpConnectionInfo {
  /** Public MCP endpoint URL to paste into the AI client, or null if unconfigured. */
  mcpUrl: string | null;
  /** Whether the OAuth authorization server (WorkOS AuthKit) is configured. */
  authConfigured: boolean;
}

/** `GET /identity/mcp-agents` response. */
export interface ListMcpAgentsResponse {
  agents: McpAgentGrant[];
  /** All selectable scopes (single source: {@link MCP_TOOL_AUTH}). */
  scopeCatalog: McpAgentScopeCatalogEntry[];
  /** Whether per-agent enforcement is active for the tenant (org flag). */
  enforcementEnabled: boolean;
  /** Onboarding details for connecting a new agent. */
  connection: McpConnectionInfo;
}

/** Fields that can be changed on a connected AI agent grant. */
export interface UpdateMcpAgentGrantRequest {
  status?: McpAgentConnectionStatus;
  allowedScopes?: McpOAuthScope[];
  label?: string;
}

/** `PATCH /identity/mcp-agents` request body. Mutations require the opaque grantId. */
export interface UpdateMcpAgentGrantByIdRequest extends UpdateMcpAgentGrantRequest {
  grantId: string;
}

/** `DELETE /identity/mcp-agents` request body. Mutations require the opaque grantId. */
export interface DeleteMcpAgentByIdRequest {
  grantId: string;
}

// ============================================================================
// MCP "Connect" flow (WorkOS Standalone Connect — our login + permission picker)
// ============================================================================

/** JWT claim that carries the permission set the user consented to at connect. */
export const MCP_CONSENT_SCOPES_CLAIM = 'mcp_scopes' as const;

/**
 * `POST /identity/mcp/connect/complete` request body (acting user from session).
 *
 * We deliberately do NOT carry connect-time scopes here: under WorkOS Standalone
 * Connect we do not pass `user_consent_options` (it would render AuthKit's own
 * single-select tool list). Per-agent, per-tool scoping is governed afterwards
 * in Settings → Connected AI agents (the grant store).
 */
export interface CompleteMcpConnectRequest {
  /** Temporary id AuthKit passed to our Login URI. */
  externalAuthId: string;
}

/** `POST /identity/mcp/connect/complete` response — where to send the browser next. */
export interface CompleteMcpConnectResponse {
  /** AuthKit URL to redirect to so it can issue the token to the MCP client. */
  redirectUri: string;
}

/** Human labels for each MCP OAuth scope (UI catalog). */
export const MCP_OAUTH_SCOPE_LABEL: Readonly<Record<McpOAuthScope, string>> = Object.freeze({
  [McpOAuthScope.AP_BILLS_READ]: 'Read accounts-payable bills',
  [McpOAuthScope.AP_BILLS_MANAGE]: 'Update pre-approval accounts-payable bills',
  [McpOAuthScope.AP_VENDORS_READ]: 'Read vendors',
  [McpOAuthScope.AP_VENDORS_MANAGE]: 'Create and manage vendor payment destinations',
  [McpOAuthScope.AP_VENDOR_KNOWLEDGE_MANAGE]:
    'Record and change vendor tax rules, PIS/COFINS credit rules and instructions',
  [McpOAuthScope.EXPENSE_ACCRUALS_READ]: 'Read proposed expense accruals',
  [McpOAuthScope.EXPENSE_ACCRUALS_MANAGE]: 'Confirm expense accrual proposals',
  [McpOAuthScope.AP_RECEIVED_NFS_READ]: 'Read received fiscal documents (NFs)',
  [McpOAuthScope.GL_CLOSING_READ]: 'Read GL closing tasks',
  [McpOAuthScope.ORG_READ]: 'Read organization master data (entities, cost centers, departments)',
  [McpOAuthScope.GL_READ]: 'Read GL books and periods',
  [McpOAuthScope.GL_MANAGE]: 'Create and post GL manual journal entry drafts',
  [McpOAuthScope.SETTINGS_READ]: 'Read tenant settings',
  [McpOAuthScope.CASHFLOW_MANAGE]:
    'Create and manage planned cash-flow items and liquidity settings',
  [McpOAuthScope.SETTINGS_MANAGE]: 'Create and manage tenant settings (e.g. AP intake rules)',
  [McpOAuthScope.BUDGET_READ]: 'Read budgets and utilization',
  [McpOAuthScope.BUDGET_MANAGE]: 'Create and manage budgets',
  [McpOAuthScope.IDENTITY_READ]: 'Find users for ownership assignment',
  [McpOAuthScope.AR_READ]: 'Read accounts-receivable invoices, receipts, and customers',
  [McpOAuthScope.CASHFLOW_READ]: 'Read cash-flow projections, liquidity and treasury data',
  [McpOAuthScope.FPA_READ]: 'Read FP&A rollups, templates, metrics, and evaluated reports',
  [McpOAuthScope.HCM_MANAGE]: 'Register PJ collaborators and their payroll contracts',
  [McpOAuthScope.FPA_MANAGE]: 'Create and manage FP&A rollups, templates, and metrics',
  [McpOAuthScope.BILLING_READ]: 'Read the billing service catalog and contracts',
  [McpOAuthScope.BILLING_MANAGE]: 'Create, update and archive billing services',
  [McpOAuthScope.WORKFLOW_READ]: 'Read and preview workflow rules',
  [McpOAuthScope.WORKFLOW_MANAGE]: 'Create, update and delete workflow rules',
});

/** Rótulos de cada escopo OAuth do MCP em português do Brasil. */
export const MCP_OAUTH_SCOPE_LABEL_PT_BR: Readonly<Record<McpOAuthScope, string>> = Object.freeze({
  [McpOAuthScope.AP_BILLS_READ]: 'Consultar contas a pagar',
  [McpOAuthScope.AP_BILLS_MANAGE]: 'Atualizar contas a pagar antes da aprovação',
  [McpOAuthScope.AP_VENDORS_READ]: 'Consultar fornecedores',
  [McpOAuthScope.AP_VENDORS_MANAGE]: 'Criar e gerenciar destinos de pagamento de fornecedores',
  [McpOAuthScope.AP_VENDOR_KNOWLEDGE_MANAGE]:
    'Registrar e alterar regras de retenção, regras de crédito de PIS/COFINS e instruções do fornecedor',
  [McpOAuthScope.EXPENSE_ACCRUALS_READ]: 'Consultar provisões de despesa propostas',
  [McpOAuthScope.EXPENSE_ACCRUALS_MANAGE]: 'Confirmar propostas de provisão de despesa',
  [McpOAuthScope.AP_RECEIVED_NFS_READ]: 'Consultar documentos fiscais recebidos (NFs)',
  [McpOAuthScope.GL_CLOSING_READ]: 'Consultar tarefas de fechamento contábil',
  [McpOAuthScope.ORG_READ]:
    'Consultar dados cadastrais da organização (entidades, centros de custo, departamentos)',
  [McpOAuthScope.GL_READ]: 'Consultar livros e períodos contábeis',
  [McpOAuthScope.GL_MANAGE]: 'Criar e lançar rascunhos de lançamentos contábeis manuais',
  [McpOAuthScope.SETTINGS_READ]: 'Consultar configurações do tenant',
  [McpOAuthScope.CASHFLOW_MANAGE]:
    'Criar e gerenciar itens planejados de fluxo de caixa e configurações de liquidez',
  [McpOAuthScope.SETTINGS_MANAGE]:
    'Criar e gerenciar configurações do tenant (por exemplo, regras de entrada de contas a pagar)',
  [McpOAuthScope.BUDGET_READ]: 'Consultar orçamentos e utilização',
  [McpOAuthScope.BUDGET_MANAGE]: 'Criar e gerenciar orçamentos',
  [McpOAuthScope.IDENTITY_READ]: 'Buscar usuários para atribuição de responsáveis',
  [McpOAuthScope.AR_READ]: 'Consultar faturas, recebimentos e clientes de contas a receber',
  [McpOAuthScope.CASHFLOW_READ]: 'Consultar projeções de fluxo de caixa, liquidez e tesouraria',
  [McpOAuthScope.FPA_READ]:
    'Consultar consolidações, modelos, métricas e relatórios avaliados de FP&A',
  [McpOAuthScope.HCM_MANAGE]: 'Cadastrar colaboradores PJ e seus contratos de folha',
  [McpOAuthScope.FPA_MANAGE]: 'Criar e gerenciar consolidações, modelos e métricas de FP&A',
  [McpOAuthScope.BILLING_READ]: 'Consultar o catálogo de serviços de faturamento e contratos',
  [McpOAuthScope.BILLING_MANAGE]: 'Criar, atualizar e arquivar serviços de faturamento',
  [McpOAuthScope.WORKFLOW_READ]: 'Consultar e simular regras de fluxo de trabalho',
  [McpOAuthScope.WORKFLOW_MANAGE]: 'Criar, atualizar e excluir regras de fluxo de trabalho',
});

/**
 * Build the scope catalog from {@link MCP_TOOL_AUTH} so the UI list and the
 * enforced scopes can never drift. Each scope lists the tools it unlocks.
 */
export function buildMcpAgentScopeCatalog(): McpAgentScopeCatalogEntry[] {
  const toolsByScope = new Map<McpOAuthScope, string[]>();
  for (const [toolName, requirement] of Object.entries(MCP_TOOL_AUTH)) {
    const list = toolsByScope.get(requirement.scope) ?? [];
    list.push(toolName);
    toolsByScope.set(requirement.scope, list);
  }
  return Array.from(toolsByScope.entries()).map(([scope, tools]) => ({
    scope,
    label: MCP_OAUTH_SCOPE_LABEL[scope] ?? scope,
    labelPtBr: MCP_OAUTH_SCOPE_LABEL_PT_BR[scope] ?? scope,
    tools: tools.sort(),
  }));
}

/** Type guard: is `value` a known MCP OAuth scope? */
export function isMcpOAuthScope(value: string): value is McpOAuthScope {
  return (Object.values(McpOAuthScope) as string[]).includes(value);
}

/** Narrow an arbitrary string list to known MCP OAuth scopes (drops unknowns). */
export function toMcpOAuthScopes(values: readonly string[]): McpOAuthScope[] {
  return values.filter(isMcpOAuthScope);
}
